import React, { useState } from 'react'
import { useField } from 'react-final-form'
import { FieldArray } from 'react-final-form-arrays'
import { useFieldValue } from '../../../../hooks/useFieldValue'
import Alert from '@material-ui/lab/Alert'
import { useTranslation } from 'react-i18next'
import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@material-ui/core'
import { invoiceTypes } from './helpers'
import InvoiceDialog from './InvoiceDialog'
import InvoiceActivity2022Row from './InvoiceActivity2022Row'
import { useOptions } from '../../../../hooks/useOptions'
import InvoiceList from './InvoiceList'

const useMissingFields = (zpm: boolean) => {
  const { t } = useTranslation('invoiceSection')
  const treatmentId = useFieldValue('treatmentType.id')
  const isInsured = treatmentId === 1 || treatmentId === 5

  // Patient details
  const patientBirthDate = useFieldValue('patient.birthDate')
  const patientGender = useFieldValue('patient.gender')
  const patientStreet = useFieldValue('patient.street')
  const patientHouseNumber = useFieldValue('patient.houseNumber')
  const patientPostalCode = useFieldValue('patient.postalCode')
  const patientResidence = useFieldValue('patient.residence')
  const patientBsn = useFieldValue('patient.bsn')
  const patientBsnDuplicateError = useFieldValue('patient.bsnDuplicateError')

  // Program/practice settings
  const practice = useFieldValue('practice')
  const treatmentType = useFieldValue('treatmentType')
  const primaryPsychologist = useFieldValue('primaryPsychologist')
  const secondaryPsychologist = useFieldValue('secondaryPsychologist')
  const startDate = useFieldValue('startDate')
  const referrerDetailsValid = useFieldValue('referrerDetailsValid')
  const language = useFieldValue('funnel.language')
  const online = useFieldValue('funnel.online')
  const onsite = useFieldValue('funnel.onsite')

  // End/closing details (<2022)
  const endDate = useFieldValue('endDate')
  const closingReason = useFieldValue('closingReason')

  // Referrer details
  const referrerAgb = useFieldValue('referrerAgb')
  const referrerTypeId = useFieldValue('referrerType.id')
  const referralDate = useFieldValue('referralDate')
  const insurerId = useFieldValue('insurer.id')
  const insuredNumber = useFieldValue('insuranceNumber')

  // Product/performance (use ids because fields are stored under *.id)
  const productStart = useFieldValue('productStart.id')
  const productEnd = useFieldValue('productEnd.id')

  const isTreatmentType2022 = treatmentType?.id === 5

  // Compose list of REQUIRED missing fields translated to NL, joined by comma for the alert
  const requiredMissing = Object.values({
    // Patient
    birthDate: patientBirthDate ? '' : t('birthDate'),
    greeting: patientGender ? '' : t('greeting'),
    street: patientStreet ? '' : t('street'),
    houseNumber: patientHouseNumber ? '' : t('houseNumber'),
    postalCode: patientPostalCode ? '' : t('postcode'),
    residence: patientResidence ? '' : t('residence'),
    // BSN must be present and not marked as duplicate
    bsn: patientBsn && !patientBsnDuplicateError ? '' : t('bsn'),

    // Treatment/program
    treatmentType: treatmentType ? '' : t('treatmentType'),
    practice: practice ? '' : t('practice'),
    bankAccountNumber: primaryPsychologist?.practice?.bankAccountNumber
      ? ''
      : t('bankAccountNumber'),
    bankAccountName: primaryPsychologist?.practice?.bankAccountName
      ? ''
      : t('bankAccountName'),
    secondaryPsychologist: secondaryPsychologist ? '' : t('secondaryPsychologist'),
    language: language ? '' : t('language'),
    location: online || onsite ? '' : t('location'),

    // Required for insured treatments
    startDate: !isInsured || (isInsured && startDate) ? '' : t('startDate'),
    endDate:
      isTreatmentType2022 || !isInsured || (isInsured && endDate)
        ? ''
        : t('endDate'),
    closingReason:
      isTreatmentType2022 || !isInsured || (isInsured && closingReason)
        ? ''
        : t('closingReason'),

    // Referrer
    referrerType: !isInsured || (isInsured && referrerTypeId) ? '' : t('referrerType'),
    referralDate: !isInsured || (isInsured && referralDate) ? '' : t('referralDate'),
    // Require valid referrer details (AGB must resolve to a name)
    referrerAgb:
      !isInsured || (isInsured && referrerDetailsValid) ? '' : t('agbReferrer'),

    // Product (Prestatie) is always required to create an invoice
    productStart: !isInsured || productStart ? '' : t('productPerformance'),
    productEnd:
      isTreatmentType2022 || !isInsured || zpm || productEnd
        ? ''
        : t('productEnd')
  })
    .filter((field) => !!field)
    .join(', ')

  // Compose OPTIONAL missing fields (shown but not blocking invoice)
  const optionalMissing = Object.values({
    insurer: isInsured && !insurerId ? t('insurer') : '',
    insuredNumber: isInsured && !insuredNumber ? t('insuredNumber') : ''
  })
    .filter((field) => !!field)
    .join(', ')

  return { requiredMissing, optionalMissing }
}

// this component uses a lot of hooks and state
// making it a separate component avoids rerendering whole section
// whenever any of the fields used change
const AlertWrapper = ({ zpm }: { zpm: boolean }): JSX.Element => {
  const { t } = useTranslation('invoiceSection')
  const { requiredMissing, optionalMissing } = useMissingFields(zpm)
  if (!requiredMissing && !optionalMissing) return <></>
  return (
    <>
      {requiredMissing && (
        <Alert severity="warning">{t('warningText')} {requiredMissing}.</Alert>
      )}
      {optionalMissing && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          {t('optionalWarningText')} {optionalMissing}.
        </Alert>
      )}
    </>
  )
}

// this component uses a lot of hooks and state
// making it a separate component avoids rerendering whole section
// whenever any of the fields used change
type InvoiceDialogWrapperProps = {
  invoiceType: string
  selectedActivities: any[]
  setSelectedActivities: (x: any[]) => void
  zpm: boolean
}
const InvoiceDialogWrapper = ({
  invoiceType,
  selectedActivities,
  setSelectedActivities,
  zpm
}: InvoiceDialogWrapperProps): JSX.Element => {
  const { requiredMissing } = useMissingFields(zpm)
  return (
    <InvoiceDialog
      partial={invoiceType === invoiceTypes.PARTIAL}
      disabled={!invoiceType || !!requiredMissing}
      selectedActivities={selectedActivities}
      setSelectedActivities={setSelectedActivities}
    />
  )
}

const InvoiceSection = ({
  treatmentType,
  zpm
}: {
  treatmentType: number
  zpm: boolean
}) => {
  const invoiceType =
    treatmentType === 1 ? invoiceTypes.FULL : invoiceTypes.PARTIAL
  const [selectedActivities, setSelectedActivities] = useState<any>([])

  const {
    input: { value: isLegacy }
  } = useField('isLegacy')

  const {
    consultationTypeOptions,
    psychologistOptions,
    durationOptions,
    consultationPrices,
    loading: loadingOptions
  } = useOptions(isLegacy)

  const { t } = useTranslation('invoiceSection')

  const invoices = useFieldValue('invoices')
  const referralDate: string | undefined = useFieldValue('referralDate')
  const startDate: string | undefined = useFieldValue('startDate')

  const isReferralAfterStart = (() => {
    if (!referralDate || !startDate) return false
    const r = new Date(referralDate)
    r.setHours(0, 0, 0, 0)
    const s = new Date(startDate)
    s.setHours(0, 0, 0, 0)
    return r > s
  })()

  const handleActivitySelect = (isChecked: boolean, activity: any) => {
    if (isChecked) {
      setSelectedActivities((prevState: any[]) => [...prevState, activity])
    } else {
      setSelectedActivities((prevState: any[]) =>
        prevState.filter((el: any) => el.id !== activity.id)
      )
    }
  }

  if (loadingOptions) return <CircularProgress />

  return (
    <div>
      <AlertWrapper zpm={zpm} />
      {isReferralAfterStart && (
        <Alert severity="warning" style={{ marginTop: 8 }}>
          {t('referralDateAfterStartDate')}
        </Alert>
      )}

      <Table size="small" style={{ marginBottom: 20 }}>
        <TableHead>
          <TableRow>
            <TableCell></TableCell>
            <TableCell>{t('dateLabel')}</TableCell>
            <TableCell>{t('consultType')}</TableCell>
            <TableCell>{t('practitioner')}</TableCell>
            <TableCell>{t('duration')}&nbsp;(m)</TableCell>
            <TableCell>{t('amount')}</TableCell>
            <TableCell>{t('previouslyInvoiced')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <FieldArray name="activities2022">
            {({ fields }) => {
              return (
                <>
                  {fields.map((name) => {
                    const isSelected = !!selectedActivities.filter(
                      (el: any) => el.name === name
                    ).length
                    return (
                      <InvoiceActivity2022Row
                        key={name}
                        name={name}
                        consultationTypes={consultationTypeOptions}
                        psychologistOptions={psychologistOptions}
                        durationOptions={durationOptions}
                        prices={consultationPrices}
                        handleActivitySelect={handleActivitySelect}
                        isSelected={isSelected}
                      />
                    )
                  })}
                </>
              )
            }}
          </FieldArray>
        </TableBody>
      </Table>

      <InvoiceDialogWrapper
        invoiceType={invoiceType}
        zpm={zpm}
        selectedActivities={selectedActivities}
        setSelectedActivities={setSelectedActivities}
      />

      {invoices && <InvoiceList invoices={invoices} />}
    </div>
  )
}

export default InvoiceSection
